/**
 * Phase 105-Agent-C — useOrderAgentPin hook.
 *
 * Customer-side: polls `/api/orders/{tid}/agent-location` every 15 sec
 * while the order is in an "agent-active" lifecycle status. Returns
 * `{lat, lng, stale, lastUpdatedAt}` shaped to drop into the existing
 * `LiveTrackingMap` `agent` prop. Returns `{lat: null, lng: null}` on
 * 404 (no agent yet) so the map doesn't render a phantom pin.
 *
 * Pause condition: `enabled=false` halts polling so the hook doesn't
 * thrash when the customer is on an idle screen.
 */
import { useEffect, useRef, useState } from "react";
import api from "../services/api";

const POLL_MS = 15_000;
const ACTIVE_STATUSES = new Set([
  // "pending" is included because the canonical PATCH /assign keeps the
  // order in `pending` status until the agent /accepts. The /agent-location
  // endpoint returns a clean 404 when no agent is on the order yet, so
  // polling during `pending` is cheap and lets the blue pin appear the
  // instant the agent shares a location after admin-assigns them.
  "pending",
  "assigned", "pickup_assigned", "accepted",
  "picked_up", "picked", "in_transit", "at_origin_hub", "at_dest_hub",
  "linehaul", "out_for_delivery",
]);

export default function useOrderAgentPin(trackingId, status, { enabled = true } = {}) {
  const [pin, setPin] = useState({ lat: null, lng: null, stale: false, lastUpdatedAt: null });
  const timerRef = useRef(null);

  useEffect(() => {
    const isActive = enabled && trackingId && ACTIVE_STATUSES.has(String(status || "").toLowerCase());
    if (!isActive) {
      setPin({ lat: null, lng: null, stale: false, lastUpdatedAt: null });
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/orders/${trackingId}/agent-location`);
        if (cancelled) return;
        setPin({
          lat:           data?.lat ?? null,
          lng:           data?.lng ?? null,
          stale:         !!data?.stale,
          lastUpdatedAt: data?.last_updated_at ?? null,
          ageSeconds:    data?.age_seconds ?? null,
        });
      } catch (e) {
        // 404 = no agent yet OR no location yet — render no pin.
        if (e?.response?.status === 404) {
          if (!cancelled) setPin({ lat: null, lng: null, stale: false, lastUpdatedAt: null });
        }
        // 403 means the customer shouldn't see this — also no pin.
      }
    };
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [trackingId, status, enabled]);

  return pin;
}
